import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getLaunchAccount, sendClaimEmail } from '@/api/launch';
import { passwordReset } from '@/api/auth';

/**
 * An account made from the MyStateMLS button has no password the member knows: they come back
 * through that button. This lets them choose one, so they can also sign in at breadlist.site.
 * Same code email as a password reset, finished right here (the /password-reset page is for
 * signed-out visitors). Renders nothing for every other account, and once a password is set.
 */
// Where the code went, kept for this tab so a reload between "Set a password" and the code
// does not make the member send another email. Storage can be blocked; the banner works without it.
const SENT_KEY = 'blt_claim_code_sent_to';
function readSentTo(): string | null {
  try {
    return sessionStorage.getItem(SENT_KEY);
  } catch {
    return null;
  }
}
function writeSentTo(email: string | null): void {
  try {
    if (email) sessionStorage.setItem(SENT_KEY, email);
    else sessionStorage.removeItem(SENT_KEY);
  } catch {
    /* storage blocked */
  }
}

export function ClaimAccountBanner() {
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ['launch-account'], queryFn: getLaunchAccount, retry: 0, staleTime: 5 * 60_000 });
  const [sentTo, setSentToState] = useState<string | null>(readSentTo);
  const setSentTo = (email: string | null) => {
    writeSentTo(email);
    setSentToState(email);
  };
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const send = useMutation({ mutationFn: sendClaimEmail, onSuccess: (res) => setSentTo(res.email) });
  const save = useMutation({
    mutationFn: () => passwordReset(sentTo ?? '', code.trim(), password),
    onSuccess: () => {
      writeSentTo(null);
      return queryClient.invalidateQueries({ queryKey: ['launch-account'] });
    },
  });

  // The confirmation outlives the refetch that now says "claimed".
  if (save.isSuccess) {
    return (
      <div role="status" className="flex items-center gap-3 border-b border-brand-border bg-brand-teal-soft px-6 py-3 text-sm text-brand-ink">
        <KeyRound className="h-4 w-4 shrink-0 text-brand-teal-deep" />
        Password saved. You can now also sign in at breadlist.site with {sentTo}.
      </div>
    );
  }

  if (!data?.launched || data.claimed) return null;

  return (
    <div role="status" className="flex flex-wrap items-center gap-3 border-b border-brand-border bg-brand-lilac-soft px-6 py-3 text-sm text-brand-ink">
      <KeyRound className="h-4 w-4 shrink-0 text-brand-plum" />
      {!sentTo ? (
        <>
          <span>You signed in from MyStateMLS. Choose a password so you can also sign in at breadlist.site.</span>
          <Button
            size="sm"
            className="ml-auto bg-brand-plum hover:bg-brand-plum-deep"
            disabled={send.isPending}
            onClick={() => send.mutate()}
          >
            {send.isPending ? 'Sending…' : 'Set a password'}
          </Button>
        </>
      ) : (
        <form
          className="flex min-w-0 flex-1 flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <span className="mr-1">
            We emailed a code to <strong>{sentTo}</strong>.
          </span>
          <Input
            aria-label="Code from the email"
            placeholder="Code"
            inputMode="numeric"
            autoComplete="one-time-code"
            className="h-8 w-24 bg-white"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <Input
            aria-label="New password"
            placeholder="New password (8+ characters)"
            type="password"
            autoComplete="new-password"
            className="h-8 w-56 bg-white"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button
            type="submit"
            size="sm"
            className="bg-brand-plum hover:bg-brand-plum-deep"
            disabled={save.isPending || code.trim().length < 4 || password.length < 8}
          >
            {save.isPending ? 'Saving…' : 'Save password'}
          </Button>
          <button
            type="button"
            className="text-xs text-brand-plum underline underline-offset-2"
            disabled={send.isPending}
            onClick={() => send.mutate()}
          >
            {send.isPending ? 'Sending…' : 'Send a new code'}
          </button>
        </form>
      )}
      {send.isError && <span className="w-full text-xs text-brand-error">Could not send the email. Please try again.</span>}
      {save.isError && (
        <span className="w-full text-xs text-brand-error">
          {save.error instanceof Error ? save.error.message : 'That code did not work. Check it and try again.'}
        </span>
      )}
    </div>
  );
}
